import React, { useEffect, Suspense } from 'react'

import { useSetRecoilState } from 'recoil'
import { gameState, modalState, userState } from './store'
import { useToast } from '@chakra-ui/react'

import Layout from './Layout'

import socket from './socket'
import { numberWithCommas } from './utils'

const UserModal = React.lazy(() => import('./UserModal'))
const RegisterModal = React.lazy(() => import('./RegisterModal'))
const LoginModal = React.lazy(() => import('./LoginModal'))
const DepositModal = React.lazy(() => import('./DepositModal'))
const WithdrawModal = React.lazy(() => import('./WithdrawModal'))
const TransactionsModal = React.lazy(() => import('./TransactionsModal'))
const SupportModal = React.lazy(() => import('./SupportModal'))
const HelpModal = React.lazy(() => import('./HelpModal'))
const AffiliateModal = React.lazy(() => import('./AffiliateModal'))
const TosModal = React.lazy(() => import('./TosModal'))
const SettingsModal = React.lazy(() => import('./SettingsModal'))
const ForgotPasswordModal = React.lazy(() => import('./ForgotPasswordModal'))
const GameModal = React.lazy(() => import('./GameModal'))

function App() {
  const setUser = useSetRecoilState(userState)
  const setModal = useSetRecoilState(modalState)
  const setGame = useSetRecoilState(gameState)

  const toast = useToast()

  useEffect(() => {
    socket.on(
      'app-init',
      ({
        game: {
          startDate,
          endDate,
          state,
          result,
          id,
          history,
          bets,
          maxBet,
          minBet,
          elapsed,
          maxProfit,
        },
        user,
      }) => {
        user &&
          setUser(prevUser => {
            const bet = bets.find(x => x.username === user.username)
            const isBetting = bet && !bet.multiplier && state !== 'CRASHED'
            return {
              ...prevUser,
              ...user,
              isLoggedIn: true,
              ...(isBetting && {
                currentBet: bet.amount,
                state: 'BETTING',
              }),
            }
          })

        setGame(prevGame => ({
          ...prevGame,
          startDate: startDate ? new Date(Date.now() - elapsed) : null,
          endDate: endDate ? new Date(endDate) : null,
          bets: bets.sort((a, b) => b.amount - a.amount),
          result,
          state,
          history,
          id,
          maxBet,
          minBet,
          maxProfit,
        }))
      }
    )

    socket.on('game-start', () => {
      setGame(prevGame => ({ ...prevGame, state: 'STARTED', elapsed: 0 }))
    })

    socket.on('user-deposit', amount => {
      setUser(prevState => {
        return {
          ...prevState,
          balance: prevState.balance + amount,
        }
      })
      toast({
        title: `${numberWithCommas(amount)} XRP has been added your balance!`,
        status: 'success',
        duration: 9000,
        isClosable: true,
      })
      setModal(null)
    })
    socket.on('game-crash', ({ hash, result, endDate }) => {
      setGame(prevState => {
        const history = [
          { result, id: prevState.id, hash, bets: prevState.bets },
          ...prevState.history,
        ]

        history.length > 25 && history.pop()

        return {
          ...prevState,
          state: 'CRASHED',
          endDate,
          result,
          history,
        }
      })
      setTimeout(() => {
        setUser(prevState => {
          return {
            ...prevState,
            currentBet: null,
            state: 'IDLE',
          }
        })
      }, 25)
    })

    socket.on('game-tick', elapsed => {
      /** Correct the time of startTime every gameTick **/
      setGame(prevGame => {
        if (!prevGame.startDate) return prevGame

        const latencyStartDate = new Date(Date.now() - elapsed)

        if (prevGame.startDate < latencyStartDate) return prevGame

        return {
          ...prevGame,
          startDate: latencyStartDate,
        }
      })
    })

    socket.on('game-waiting', ({ startDate, maxProfit }) => {
      setGame(prevGame => ({
        ...prevGame,
        startDate,
        state: 'WAITING',
        bets: [],
        endDate: null,
        result: null,
        id: prevGame.id + 1,
        elapsed: -5000,
        maxProfit,
      }))
    })
    socket.on('disconnect', () => {
      setGame({
        id: null,
        state: 'LOADING',
        startDate: null,
        endDate: null,
        result: null,
        bets: [],
        history: [],
      })
    })
    socket.on('user-tip', ({ amount, username }) => {
      setUser(prevState => {
        return {
          ...prevState,
          balance: prevState.balance + amount,
        }
      })
      toast({
        title: `${username} has tipped you ${numberWithCommas(amount)} XRP!`,
        status: 'success',
        duration: 9000,
        isClosable: true,
      })
      setModal(null)
    })
    return () => {
      socket.off('app-init')
      socket.off('user-deposit')
      socket.off('game-crash')
      socket.off('game-waiting')
      socket.off('disconnect')
      socket.off('game-tick')
    }
  }, [])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const referralParam = urlParams.get('r')
    if (!referralParam) return
    localStorage.setItem('referredBy', referralParam)
    urlParams.delete('r')
    window.history.replaceState(null, '', '/')
  }, [])

  return (
    <>
      <Layout />

      {/* MODALS */}
      <Suspense fallback={<></>}>
        <RegisterModal />
        <LoginModal />
        <HelpModal />
        <SupportModal />
        <DepositModal />
        <TransactionsModal />
        <WithdrawModal />
        <AffiliateModal />
        <TosModal />
        <SettingsModal />
        <ForgotPasswordModal />
        <GameModal />
        <UserModal />
      </Suspense>
    </>
  )
}

export default App
